import React from 'react';
import { Star } from 'lucide-react';
import type { TimelineEpic, Objective, Milestone } from '@/types';

interface TimelineBarProps {
  epic: TimelineEpic;
  objective: Objective;
  selectedYear: number;
}

const TimelineBar: React.FC<TimelineBarProps> = ({ epic, selectedYear }) => {
  // Get all milestones for this epic
  const milestones = epic.milestones || [];

  // Debug logging
  console.log('TimelineBar - Epic:', epic.name);
  console.log('TimelineBar - Milestones count:', milestones.length);
  console.log('TimelineBar - Selected year:', selectedYear);
  console.log('TimelineBar - Milestones:', milestones);

  if (milestones.length === 0) {
    console.log('TimelineBar - No milestones found');
    return (
      <div className="text-xs text-[var(--muted)]">No milestones</div>
    );
  }

  // Filter milestones for the selected year
  const yearMilestones = milestones.filter((milestone) => {
    const date = new Date(milestone.targetDate);
    const milestoneYear = date.getFullYear();
    console.log(`Milestone "${milestone.description}" date: ${date.toISOString()}, year: ${milestoneYear}, selected: ${selectedYear}`);
    return milestoneYear === selectedYear;
  });

  console.log('TimelineBar - Year milestones count:', yearMilestones.length);

  if (yearMilestones.length === 0) {
    return (
      <div className="text-xs text-[var(--muted)]">
        No milestones in FY{selectedYear} (Have {milestones.length} in other years)
      </div>
    );
  }

  // Calculate the start and end dates
  // Bar always starts from January 1st and ends at the latest milestone
  const milestoneDates = yearMilestones.map((m) => new Date(m.targetDate));
  const startDate = new Date(selectedYear, 0, 1); // January 1st of selected year
  const endDate = new Date(Math.max(...milestoneDates.map((d) => d.getTime())));

  // Calculate positions as percentages of the year
  const yearStart = new Date(selectedYear, 0, 1);
  const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59);
  const yearDuration = yearEnd.getTime() - yearStart.getTime();

  const getPositionPercent = (date: Date): number => {
    const offset = date.getTime() - yearStart.getTime();
    return (offset / yearDuration) * 100;
  };

  const barStartPercent = getPositionPercent(startDate);
  const barEndPercent = getPositionPercent(endDate);
  const barWidthPercent = barEndPercent - barStartPercent;

  // Sort milestones by date
  const sortedMilestones = [...yearMilestones].sort(
    (a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()
  );

  return (
    <div className="relative h-8">
      {/* Timeline Bar Background Grid (12 months) */}
      <div className="absolute inset-0 grid grid-cols-12">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="border-r border-[var(--border)] opacity-20 last:border-r-0"
          />
        ))}
      </div>

      {/* Timeline Bar */}
      <div
        className="absolute top-1/2 -translate-y-1/2 h-6 bg-[var(--accent)]/70 rounded"
        style={{
          left: `${barStartPercent}%`,
          width: `${Math.max(barWidthPercent, 0.5)}%`,
        }}
      />

      {/* Milestone Markers */}
      {sortedMilestones.map((milestone, index) => {
        const milestoneDate = new Date(milestone.targetDate);
        const positionPercent = getPositionPercent(milestoneDate);

        // Truncate description for display
        const displayText = milestone.description || `Milestone ${index + 1}`;
        const truncatedText = displayText.length > 20
          ? displayText.substring(0, 20) + '...'
          : displayText;

        return (
          <div
            key={milestone.id}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
            style={{ left: `${positionPercent}%` }}
          >
            {/* Star Icon */}
            <div
              className="relative flex items-center justify-center"
              title={`${displayText} - ${milestoneDate.toLocaleDateString()}`}
            >
              <Star
                className="w-5 h-5 fill-yellow-400 text-yellow-500 drop-shadow-md cursor-pointer hover:scale-110 transition-transform"
              />
            </div>

            {/* Milestone Label */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded px-2 py-1 shadow-lg">
                <div className="text-xs font-medium text-[var(--text)]">
                  {truncatedText}
                </div>
                <div className="text-xs text-[var(--muted)]">
                  {milestoneDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                {milestone.type && (
                  <div className="text-xs text-[var(--accent)] mt-0.5">
                    {milestone.type}
                  </div>
                )}
              </div>
            </div>

            {/* Static Label Below Timeline (visible on desktop) */}
            <div className="hidden lg:block absolute top-7 left-1/2 -translate-x-1/2 text-xs text-[var(--muted)] whitespace-nowrap">
              {truncatedText}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TimelineBar;
